import axios from 'axios'
import {
  ListResponse,
  SimpleResponse,
} from '../interfaces/Shipment/ListResponseDto'
import {
  businessUnitMapper,
  mapBUDtoToBUInterface,
  mapBusinessUnitDtoListToBusinessUnitInterface,
  serviceDjangoToServiceDTO,
  storeDjangoToBUInterface,
} from '../utils/mappers'
import {
  Nullable,
  BusinessUnitDTO,
  BusinessUnitInterface,
  ServiceDTO,
  ServiceDjangoDto,
  PaymentModeId,
  DeliveryEnum,
  StoreDjangoDto,
} from '../interfaces'
import { BusinessUnitDTOV2 } from '../interfaces/BusinessUnit/BusinessUnitInterface'
import { apiDjango } from './ApiDjango'

const { REACT_APP_URL_PATH_API, REACT_APP_URL_PATH_API_DJANGO } = process.env

// Carga masiva (API Django). GET /api/store — listado de tiendas. Lanza en caso de fallo
// para que el thunk del sub-slice lo capture. Nota: el DTO Django no trae buid ni buTypeID;
// dedupe/filtra por buCode aguas abajo (ver businessUnitOptions en ShipmentBulkUpload).
export const getStoresDjango = async (
  params?: Record<string, unknown>
): Promise<BusinessUnitInterface[]> => {
  try {
    const resp = await apiDjango.get(`${REACT_APP_URL_PATH_API_DJANGO}/store`, {
      params,
    })
    if (resp.status === 200 && resp.data && resp.data.error === false) {
      const list = (resp.data.data ?? []) as StoreDjangoDto[]
      return list.map(storeDjangoToBUInterface)
    }
    console.error('getStoresDjango: respuesta con error', {
      code: resp.data?.code,
      message: resp.data?.message,
    })
    throw new Error(resp.data?.message || 'No se pudieron obtener las tiendas')
  } catch (error) {
    console.error('getStoresDjango: fallo', { error })
    throw error
  }
}

// Carga masiva (API Django). GET /api/store/coverage?latitude=&longitude= — tienda que
// cubre unas coordenadas. Drop-in de getBusinessUnitByTerritory: misma firma
// (longitude, latitude) y mismo retorno SimpleResponse<BusinessUnitInterface>. NO lanza.
// `signal` permite abortar (Review dispara hasta 200 llamadas; cancelar en cleanup).
export const getStoreByCoverageDjango = async (
  longitude: number,
  latitude: number,
  signal?: AbortSignal
): Promise<SimpleResponse<BusinessUnitInterface>> => {
  try {
    const resp = await apiDjango.get(
      `${REACT_APP_URL_PATH_API_DJANGO}/store/coverage`,
      { params: { latitude, longitude }, signal }
    )
    if (resp.status === 200 && resp.data && resp.data.error === false) {
      const store = resp.data.data as StoreDjangoDto | null
      return {
        model: store ? storeDjangoToBUInterface(store) : null,
        didError: false,
        message: resp.data.message ?? '',
        errorMessage: '',
      }
    }
    const errMessage =
      resp.data?.message ||
      resp.data?.error_data?.error ||
      'No se pudo obtener la tienda de cobertura del punto especificado'
    return {
      model: null,
      didError: true,
      message: errMessage,
      errorMessage: errMessage,
    }
  } catch (error) {
    // Aborto de la peticion (cleanup del useEffect): no es un error de negocio.
    if ((error as any)?.name === 'CanceledError' || (error as any)?.code === 'ERR_CANCELED') {
      return {
        model: null,
        didError: true,
        message: 'Consulta de cobertura cancelada',
        errorMessage: 'Consulta de cobertura cancelada',
      }
    }
    console.error('getStoreByCoverageDjango: fallo', { longitude, latitude, error })
    return {
      model: null,
      didError: true,
      message: 'No se pudo obtener la tienda de cobertura del punto especificado',
      errorMessage:
        'No se pudo obtener la tienda de cobertura del punto especificado',
    }
  }
}

export const getBusinessUnits = async (): Promise<
  Nullable<BusinessUnitInterface[]>
> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/BusinessUnitV2?status=1&IsNational=false&excludeOperationLocked=true`
  try {
    const response = await axios.get(uri)
    if (response.status === 200) {
      const storeDto = response.data
      var datamap = storeDto?.map((dto: BusinessUnitDTO) =>
        businessUnitMapper(dto)
      )
      return datamap
    }
  } catch (error) {}
  return null
}

export const getBusinessUnitsType = async (
  type: number
): Promise<Nullable<BusinessUnitInterface[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/BusinessUnitV2?status=1&IsNational=false&excludeOperationLocked=true`
  try {
    const response = await axios.get(uri)
    if (response.status === 200) {
      const storeDto = response.data
      var stoteDtoBu = storeDto as BusinessUnitDTOV2[]
      stoteDtoBu = stoteDtoBu.filter(
        (x) => x.buTypeID?.toString() === type.toString()
      )
      var datamap = stoteDtoBu?.map((dto: BusinessUnitDTO) =>
        businessUnitMapper(dto)
      )
      return datamap
    }
  } catch (error) {}
  return null
}

export const getBusinessUnitsPolly = async (): Promise<
  Nullable<BusinessUnitInterface[]>
> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/BusinessUnitV2?status=1&IsNational=false&withPolygonsList=true&excludeOperationLocked=true`
  try {
    const response = await axios.get(uri)
    if (response.status === 200) {
      const storeDto = response.data
      var datamap = storeDto?.map((dto: BusinessUnitDTO) =>
        businessUnitMapper(dto)
      )
      return datamap.filter((bu: BusinessUnitInterface) => bu.buCode != '8110')
    }
  } catch (error) {}

  return null
}

export const getBusinessUnitByTerritory = async (
  longitude: number,
  latitude: number
): Promise<SimpleResponse<BusinessUnitInterface>> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/GetBusinessUnitByTerritory?longitude=${longitude}&latitude=${latitude}`
  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      const storeDto = response.data.model
      if (storeDto != null) {
        response.data.model = mapBUDtoToBUInterface(storeDto)
      }
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudo obtener la tienda de cobertura del punto especificado',
    errorMessage:
      'No se pudo obtener la tienda de cobertura del punto especificado',
  }
}

export const getBusinessUnitsInnovus = async (): Promise<
  Nullable<BusinessUnitInterface[]>
> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/BusinessUnitV2?status=1&withPolygonsList=false&IsNational=false&includePlatforms=true`
  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      const storeDto = response.data
      return mapBusinessUnitDtoListToBusinessUnitInterface(storeDto)
    }
  } catch (error) {}

  return null
}

// ---------------------------------------------------------------------------
// Servicios y relacion ciudad↔tienda (API Django). Los tres getters de servicios son
// drop-in de sus equivalentes .NET de abajo: misma firma y mismo retorno
// ListResponse<ServiceDTO[]>, para que los consumidores solo cambien el import.
// Envelope Django: { code, error, message, data, error_data } (HTTP 200 salvo 403).
// ---------------------------------------------------------------------------
async function serviceListDjango(
  path: string,
  // `URLSearchParams` además del objeto plano: el filtro `service_type` del
  // endpoint de servicios se REPITE, y axios serializa los arrays con corchetes
  // (`service_type[]=a`), que el backend no entiende. El interceptor de
  // `apiDjango` ya sabe mergear `lang=es` sobre URLSearchParams.
  params: Record<string, unknown> | URLSearchParams,
  errMsg: string
): Promise<ListResponse<ServiceDTO[]>> {
  try {
    const resp = await apiDjango.get(
      `${REACT_APP_URL_PATH_API_DJANGO}${path}`,
      { params }
    )
    if (resp.status === 200 && resp.data && resp.data.error === false) {
      const list = (resp.data.data ?? []) as ServiceDjangoDto[]
      return {
        model: list.map(serviceDjangoToServiceDTO),
        didError: false,
        message: resp.data.message ?? '',
        errorMessage: '',
      }
    }
    const m = resp.data?.message || errMsg
    console.error(`serviceListDjango: respuesta con error en ${path}`, {
      code: resp.data?.code,
      message: resp.data?.message,
    })
    return { model: null, didError: true, message: m, errorMessage: m }
  } catch (error) {
    const m = (error as any)?.response?.data?.message || errMsg
    console.error(`serviceListDjango: fallo ${path}`, { error })
    return { model: null, didError: true, message: m, errorMessage: m }
  }
}

export type ServiceTypeFilter = 'national' | 'international'

export interface ServiceQuery {
  /**
   * ID de modalidad de pago. Ojo: el parámetro es `payment_mode`, SIN `_id`.
   *
   * Acepta string además de number porque `PaymentModeId` es un enum de STRINGS
   * con valores numéricos (`CREDIT = '3'`). Por query string da igual, y pedirle
   * al llamador que castee solo agregaría ruido.
   */
  paymentMode?: number | string
  /** ID de tipo de entrega. Parámetro `delivery_type`, SIN `_id`. */
  deliveryType?: number | string
  /** Código de línea de facturación. Obligatorio para clientes corporativos. */
  accountCode?: string
  /**
   * Nacional / internacional. **Omitirlo devuelve SOLO nacionales**, no todos:
   * para traer ambos hay que pedir los dos explícitamente.
   */
  serviceType?: ServiceTypeFilter[]
  /** Excluye servicios bloqueados para esa zona. */
  tier?: number
  /** Excluye servicios bloqueados para esa tienda. */
  store?: string
}

/**
 * GET /api/service — servicios activos, con filtros combinables.
 *
 * Reemplaza a los tres endpoints anteriores, que ahora devuelven 404 y no tienen
 * período de convivencia:
 *
 *   /service/by-payment-mode?payment_mode_id=   → /service?payment_mode=
 *   /service/by-delivery-type?delivery_type_id= → /service?delivery_type=
 *   /service/by-delivery-type?is_international=1 → /service?service_type=international
 *   /service/locked-by-application              → ya no existe: el bloqueo se
 *                                                  resuelve con `tier` y `store`
 *
 * TRES TRAMPAS DEL CONTRATO
 *
 * 1. `service_type` se REPITE, no se separa por comas:
 *    `?service_type=national&service_type=international`. Por eso se arma con
 *    `URLSearchParams` y no con el objeto `params` de axios — axios serializa los
 *    arrays como `service_type[]=a&service_type[]=b`, que el backend no entiende.
 * 2. Omitir `service_type` devuelve SOLO nacionales. No es "todos". Quien quiera
 *    internacionales tiene que pedirlos.
 * 3. Los parámetros perdieron el sufijo `_id`: es `payment_mode`, no
 *    `payment_mode_id`.
 *
 * El servicio de casillero nacional siempre se excluye del lado del backend.
 *
 * Reglas extra para clientes corporativos no admin (las aplica el backend):
 * omitir `payment_mode` asume crédito; mandar otra modalidad falla con
 * `payment.PaymentMode.3`; y `account_code` es obligatorio (`account.AccountSite.5`).
 */
export const getServicesDjango = (
  query: ServiceQuery = {}
): Promise<ListResponse<ServiceDTO[]>> => {
  const params = new URLSearchParams()
  if (query.paymentMode != null)
    params.set('payment_mode', String(query.paymentMode))
  if (query.deliveryType != null)
    params.set('delivery_type', String(query.deliveryType))
  if (query.accountCode) params.set('account_code', query.accountCode)
  if (query.tier != null) params.set('tier', String(query.tier))
  if (query.store) params.set('store', query.store)
  // `append`, no `set`: cada tipo va como su propio par clave=valor.
  query.serviceType?.forEach((t) => params.append('service_type', t))

  return serviceListDjango(
    '/service',
    params,
    'No se pudieron obtener los servicios'
  )
}

// La relacion ciudad↔tienda ya no vive aqui: GET /api/business-unit-location fue eliminado
// del API y su dato (la tienda destino de cada ciudad) viene ahora en
// default_destination_store de GET /api/location → getLocationsWithStoreDjango
// (LocationServices).

export const getServicesFromPaymentMode = async (
  paymentModeID: PaymentModeId
): Promise<ListResponse<ServiceDTO[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/getServicesFromPaymentMode?PaymentModeID=${paymentModeID}`

  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudieron obtener los servicios',
    errorMessage: 'No se pudieron obtener los servicios',
  }
}

export const getServicesFromDeliveryType = async (
  deliveryTypeID: DeliveryEnum
): Promise<ListResponse<ServiceDTO[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/GetServicesFromDeliveryType?DeliveryTypeID=${deliveryTypeID}`

  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudieron obtener los servicios',
    errorMessage: 'No se pudieron obtener los servicios',
  }
}

export const getLockedServicesByTier = async (
  tierID: number
): Promise<ListResponse<ServiceDTO[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/GetLockedServicesByTier?TierID=${tierID}`

  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudieron obtener los servicios bloqueados',
    errorMessage: 'No se pudieron obtener los servicios bloqueados',
  }
}

export const getLockBUServices = async (
  buCode: string
): Promise<ListResponse<ServiceDTO[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/GetLockBUServices?BUCode=${buCode}`

  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudieron obtener los servicios bloqueados',
    errorMessage: 'No se pudieron obtener los servicios bloqueados',
  }
}

export const getAppLockedServices = async (
  applicationID: number
): Promise<ListResponse<ServiceDTO[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/GetAppLockedServices?ApplicationID=${applicationID}`

  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudieron obtener los servicios bloqueados',
    errorMessage: 'No se pudieron obtener los servicios bloqueados',
  }
}

export const getService = async (
  serviceID: number
): Promise<ListResponse<ServiceDTO>> => {
  const uri = `${REACT_APP_URL_PATH_API}/GetService?ServiceID=${serviceID}`

  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudo obtener el servicio',
    errorMessage: 'No se pudo obtener el servicio',
  }
}
